<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import AdminSectorForm from '@/views/forms/AdminSectorForm.vue'
import AdminBrandDetail from '@/views/AdminBrandDetail.vue'
import { useAdmin } from '@/stores/admin'
import type { Sector, Brand } from '@/stores/admin'
import { handleError } from '@/lib/error'
import { activeFilterUi } from '@/lib/filterUtils'

const props = defineProps<{
  sector: Sector | null
}>()

const emit = defineEmits<{
  close: []
  deleted: []
  updated: [sector: Sector]
}>()

const admin = useAdmin()

// Loading state
const isLoadingBrands = ref(false)

// Edit state
const isEditingSector = ref(false)
const showDeleteConfirm = ref(false)

// Brands in this sector
const sectorBrands = ref<Brand[]>([])

// Brand add menu
const showAddBrandMenu = ref(false)
const brandSearchQuery = ref('')
const isAddingBrand = ref(false)

// Set of brand IDs currently in this sector
const sectorBrandIds = computed(() => new Set(sectorBrands.value.map((b) => b.id)))

// Check if a brand is in this sector
function isBrandInSector(brandId: string): boolean {
  return sectorBrandIds.value.has(brandId)
}

// Clear search when popover closes
watch(showAddBrandMenu, (open) => {
  if (!open) {
    brandSearchQuery.value = ''
  }
})

// Filtered brands for the dropdown
const filteredBrands = computed(() => {
  if (!brandSearchQuery.value) {
    return admin.brands
  }
  const query = brandSearchQuery.value.toLowerCase()
  return admin.brands.filter((b) => b.name.toLowerCase().includes(query))
})

// Load brands when sector changes
watch(
  () => props.sector?.id,
  async (sectorId) => {
    if (!sectorId) {
      sectorBrands.value = []
      return
    }
    isLoadingBrands.value = true
    sectorBrands.value = []
    try {
      sectorBrands.value = await admin.fetchSectorBrands(sectorId)
    } catch (err) {
      handleError(err, 'Failed to load brands')
    } finally {
      isLoadingBrands.value = false
    }
  },
  { immediate: true },
)

// Update sector
async function handleUpdateSector(data: { name: string; emoji?: string }) {
  if (!props.sector) return
  const sectorId = props.sector.id
  try {
    await admin.updateSector(sectorId, { name: data.name, emoji: data.emoji })
    await admin.fetchSectors()
    const updated = admin.sectors.find((s) => s.id === sectorId)
    if (updated) {
      emit('updated', updated)
    }
    isEditingSector.value = false
  } catch (err) {
    handleError(err, 'Failed to update sector')
  }
}

// Delete sector
async function handleDeleteSector() {
  if (!props.sector) return
  try {
    await admin.deleteSector(props.sector.id)
    await admin.fetchBrands() // Refresh brands to reflect removed sector associations
    showDeleteConfirm.value = false
    emit('deleted')
  } catch (err) {
    handleError(err, 'Failed to delete sector')
  }
}

// Brand assignment actions
async function handleAddBrand(brandId: string) {
  if (!props.sector) return
  const sectorId = props.sector.id
  try {
    await admin.addBrandSector(brandId, sectorId)
    sectorBrands.value = await admin.fetchSectorBrands(sectorId)
    await admin.fetchSectors()
    const updated = admin.sectors.find((s) => s.id === sectorId)
    if (updated) {
      emit('updated', updated)
    }
  } catch (err) {
    handleError(err, 'Failed to add brand to sector')
  }
}

async function handleRemoveBrand(brandId: string) {
  if (!props.sector) return
  const sectorId = props.sector.id
  try {
    await admin.removeBrandSector(brandId, sectorId)
    sectorBrands.value = await admin.fetchSectorBrands(sectorId)
    await admin.fetchSectors()
    const updated = admin.sectors.find((s) => s.id === sectorId)
    if (updated) {
      emit('updated', updated)
    }
  } catch (err) {
    handleError(err, 'Failed to remove brand from sector')
  }
}

async function onBrandToggle(brandId: string) {
  if (isAddingBrand.value) return
  isAddingBrand.value = true
  try {
    if (isBrandInSector(brandId)) {
      await handleRemoveBrand(brandId)
    } else {
      await handleAddBrand(brandId)
    }
  } catch (err) {
    handleError(err, 'Failed to update brand sector')
  } finally {
    isAddingBrand.value = false
  }
}

// Brand slideover state
const showBrandSlideover = ref(false)
const selectedBrand = ref<Brand | null>(null)

function openBrandSlideover(brand: Brand) {
  selectedBrand.value = brand
  showBrandSlideover.value = true
}

function closeBrandSlideover() {
  showBrandSlideover.value = false
  selectedBrand.value = null
}

function handleBrandUpdated(brand: Brand) {
  selectedBrand.value = brand
  // Update brand in local list
  const idx = sectorBrands.value.findIndex((b) => b.id === brand.id)
  if (idx !== -1) {
    sectorBrands.value[idx] = brand
  }
}
</script>

<template>
  <div class="p-6 space-y-6 h-full overflow-y-auto" v-if="sector">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <span v-if="sector.emoji" class="text-3xl">{{ sector.emoji }}</span>
        <div>
          <h2 class="text-xl font-semibold">{{ sector.name }}</h2>
          <p class="text-sm text-muted">{{ sector.slug }}</p>
        </div>
      </div>
      <UButton icon="i-lucide-x" color="neutral" variant="ghost" @click="emit('close')" />
    </div>

    <!-- Sector Actions -->
    <div class="flex gap-2 flex-wrap">
      <UButton
        icon="i-lucide-pencil"
        color="neutral"
        variant="soft"
        @click="isEditingSector = true"
      >
        Edit Sector
      </UButton>
      <UButton
        icon="i-lucide-trash-2"
        color="error"
        variant="soft"
        @click="showDeleteConfirm = true"
      >
        Delete Sector
      </UButton>
    </div>

    <!-- Edit Sector Form -->
    <UCard v-if="isEditingSector">
      <template #header>
        <h3 class="text-lg font-semibold">Edit Sector</h3>
      </template>
      <AdminSectorForm
        :sector="sector"
        @submit="handleUpdateSector"
        @cancel="isEditingSector = false"
      />
    </UCard>

    <!-- Delete Confirmation Modal -->
    <UModal v-model:open="showDeleteConfirm">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Delete Sector</h3>
          </template>
          <p>Are you sure you want to delete "{{ sector?.name }}"?</p>
          <p class="text-sm text-muted mt-2">
            This will remove the sector assignment from all {{ sector?.brand_count || 0 }} brands.
          </p>
          <div class="flex justify-end gap-2 mt-4">
            <UButton color="neutral" variant="soft" @click="showDeleteConfirm = false">
              Cancel
            </UButton>
            <UButton color="error" @click="handleDeleteSector">Delete</UButton>
          </div>
        </UCard>
      </template>
    </UModal>

    <!-- Brands Section -->
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-semibold">Brands</h3>
        <UPopover v-model:open="showAddBrandMenu">
          <UButton icon="i-lucide-plus" color="primary" size="sm">Add</UButton>
          <template #content>
            <div class="p-2 w-72">
              <UInput
                v-model="brandSearchQuery"
                icon="i-lucide-search"
                placeholder="Search brands..."
                :ui="brandSearchQuery ? activeFilterUi : undefined"
                size="sm"
                class="mb-2"
                autofocus
              />
              <div class="max-h-64 overflow-y-auto">
                <div v-if="filteredBrands.length === 0" class="text-center py-4 text-muted text-sm">
                  No brands found
                </div>
                <div
                  v-for="brand in filteredBrands"
                  :key="brand.id"
                  class="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-elevated cursor-pointer"
                  :class="{ 'bg-elevated': isBrandInSector(brand.id) }"
                  @click="onBrandToggle(brand.id)"
                >
                  <div class="flex items-center gap-2">
                    <UAvatar :src="brand.logo_url ?? undefined" :alt="brand.name" size="xs" />
                    <span class="text-sm">{{ brand.name }}</span>
                  </div>
                  <UIcon
                    v-if="isBrandInSector(brand.id)"
                    name="i-lucide-check"
                    class="text-primary"
                  />
                </div>
              </div>
            </div>
          </template>
        </UPopover>
      </div>

      <div v-if="isLoadingBrands" class="flex justify-center py-8">
        <UIcon name="i-lucide-loader-2" class="animate-spin text-2xl text-muted" />
      </div>

      <div v-else-if="sectorBrands.length === 0" class="text-center py-8 text-muted">
        No brands in this sector
      </div>

      <div v-else class="space-y-2">
        <div
          v-for="brand in sectorBrands"
          :key="brand.id"
          class="flex items-center justify-between p-3 rounded-md bg-elevated"
        >
          <div class="flex items-center gap-3 flex-1">
            <UAvatar :src="brand.logo_url ?? undefined" :alt="brand.name" size="sm" />
            <div>
              <p class="font-medium">{{ brand.name }}</p>
              <p class="text-xs text-muted">{{ brand.slug }}</p>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <UButton
              icon="i-lucide-eye"
              color="neutral"
              variant="ghost"
              size="xs"
              @click="openBrandSlideover(brand)"
            />
            <UButton
              icon="i-lucide-x"
              color="error"
              variant="ghost"
              size="xs"
              @click="handleRemoveBrand(brand.id)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Brand Detail Slideover -->
    <USlideover v-model:open="showBrandSlideover" side="right">
      <template #content>
        <AdminBrandDetail
          v-if="selectedBrand"
          :brand="selectedBrand"
          @close="closeBrandSlideover"
          @deleted="closeBrandSlideover"
          @updated="handleBrandUpdated"
        />
      </template>
    </USlideover>
  </div>
</template>
